import {
    getFormattedDate,
    getReportStatus,
    getReportTypeText
} from '../../utils'
import { REPORT_STATUSES } from '../reports/constants'
import { LOCATIONS_TREE_ROOT_NODE_ID } from './constants'
import moment from 'moment'
import { createSelector } from 'reselect'

export function getLocationsTree(
    locations,
    locale,
    withBottomPaddingNodes = false
) {
    const rootLocation = {
        id: LOCATIONS_TREE_ROOT_NODE_ID,
        depth: 0,
        withBottomPaddingNodes: withBottomPaddingNodes || undefined,
        location: {
            id: LOCATIONS_TREE_ROOT_NODE_ID,
            // TODO: translate, use locale
            name: 'Locations'
        }
    }

    let baseLocationsTree = {
        ...rootLocation,
        children: []
    }

    if (!locations) {
        return {
            locationsMap: {},
            locationsTree: [baseLocationsTree],
            assignedLocationsMap: {},
            assignedLocationsTree: [baseLocationsTree]
        }
    }

    const locationsTreeData = prepareLocationsTreeData(
        locations,
        baseLocationsTree,
        withBottomPaddingNodes
    )
    const assignedLocations = locations.filter(l => l.is_assigned)
    const assignedLocationsTreeData = prepareLocationsTreeData(
        assignedLocations,
        baseLocationsTree,
        withBottomPaddingNodes
    )

    return {
        locationsTree: locationsTreeData.locationsTree,
        locationsMap: locationsTreeData.locationsMap,
        assignedLocationsTree: assignedLocationsTreeData.locationsTree,
        assignedLocationsMap: assignedLocationsTreeData.locationsMap,
        locationsIds: locationsTreeData.locationsIds,
        locationsTreeNoCustomerName:
            locationsTreeData.locationsTreeNoCustomerName
    }
}

function prepareLocationsTreeData(
    locations,
    baseLocationsTree,
    withBottomPaddingNodes = false
) {
    const { locationsMap, customersMap } = !locations
        ? {
              locationsMap: {},
              customersMap: {}
          }
        : locations.reduce(
              (acc, l) => {
                  acc.locationsMap[l.id] = {
                      id: l.id,
                      location: l,
                      isLocation: true,
                      children: []
                  }
                  if (!acc.customersMap[l.customer_id])
                      acc.customersMap[l.customer_id] = {
                          id: l.customer_id,
                          is_original: l.is_original,
                          customer_name: l.customer_name
                      }
                  return acc
              },
              {
                  locationsMap: {},
                  customersMap: {}
              }
          )

    const locationsTree = JSON.parse(JSON.stringify(baseLocationsTree))
    const locationsTreeNoCustomerName = JSON.parse(
        JSON.stringify(baseLocationsTree)
    )

    locations.forEach(l => {
        const parentId = locationsMap[l.id].location.parent_id

        if (parentId && locationsMap.hasOwnProperty(parentId)) {
            if (!locationsMap[parentId]) {
                locationsMap[parentId] = {
                    id: parentId,
                    children: []
                }
            }
            locationsMap[parentId].children.push(locationsMap[l.id])
        } else if (parentId && !locationsMap.hasOwnProperty(parentId)) {
            const fakeLocationId = `fake_root_${l.customer_id}`
            if (!locationsMap[fakeLocationId]) {
                locationsMap[fakeLocationId] = {
                    id: fakeLocationId,
                    location: {
                        id: fakeLocationId,
                        is_customer_title: true,
                        is_original: l.is_original,
                        customer_name: l.customer_name
                    },
                    children: []
                }
            }
            locationsMap[fakeLocationId].children.push(locationsMap[l.id])
            locationsTreeNoCustomerName.children.push(locationsMap[l.id])
        } else {
            if (!parentId) {
                const fakeLocationId = `fake_root_${l.customer_id}`
                locationsTree.children.push({
                    id: fakeLocationId,
                    location: {
                        id: locationsMap[l.id].location.customer_name,
                        is_customer_title: true,
                        is_original: locationsMap[l.id].location.is_original,
                        customer_name: locationsMap[l.id].location.customer_name
                    }
                })
            }

            locationsTreeNoCustomerName.children.push(locationsMap[l.id])
            locationsTree.children.push(locationsMap[l.id])
        }
    })

    Object.values(customersMap)
        .filter(c => {
            return (
                !locationsTree.children.some(
                    l => l.id === `fake_root_${c.id}`
                ) && locationsMap[`fake_root_${c.id}`]
            )
        })
        .forEach(c => {
            locationsTree.children.push(locationsMap[`fake_root_${c.id}`])
        })

    if (withBottomPaddingNodes) {
        locationsTree.children.push(
            createBottomPaddingNode(1),
            createBottomPaddingNode(2),
            createBottomPaddingNode(3)
        )
    }

    const locationsIds = locations
        ?.filter(i => i.id !== 'root')
        .map(el => ({ id: el.id, original_owner_id: el.owner_id }))

    return {
        locationsTree,
        locationsMap,
        locationsIds,
        locationsTreeNoCustomerName
    }
}

export function getPickerLocations(locations, location) {
    const pickerLocations =
        locations && location
            ? locations.reduce((acc, l) => {
                  if (
                      Number(l.customer_id) !== Number(location.customer_id) ||
                      Number(l.id) === Number(location.id)
                  ) {
                      return acc
                  }
                  acc[l.id] = l.name
                  return acc
              }, {})
            : {}
    return pickerLocations
}

export function getAvailableDevices(devices, locale) {
    if (!devices) return {}

    const availableDevices = devices.reduce((acc, d) => {
        if (!acc.hasOwnProperty(d.location_id)) {
            acc[d.location_id] = {
                id: d.location_id,
                name: d.location_name,
                isLocation: true,
                children: []
            }
        }
        acc[d.location_id].children.push(d)
        return acc
    }, {})

    const availableDevicesTree = {
        id: LOCATIONS_TREE_ROOT_NODE_ID,
        name: locale.locations.locations,
        isLocation: true,
        children: Object.values(availableDevices)
    }

    return {
        availableDevicesTree
    }
}

export function getLocationChartData(reports, locale) {
    if (!reports)
        return {
            chartData: null,
            dateDomain: null,
            deviceDomain: null
        }

    let dateRunner = moment().subtract(25, 'days')

    const tempXDatesMap = new Map()
    const tempYDevicesSet = new Set()
    const firstDateRunnerKey = dateRunner.format('DD/MM/YY')
    tempXDatesMap.set(firstDateRunnerKey, [])
    for (let i = 0; i < 25; i++) {
        dateRunner = dateRunner.add(1, 'days')
        tempXDatesMap.set(dateRunner.format('DD/MM/YY'), [])
    }

    reports.forEach(r => {
        const reportDateKey = moment(r.created_at).format('DD/MM/YY')
        if (tempXDatesMap.has(reportDateKey))
            tempXDatesMap.get(reportDateKey).push(r)
        if (!tempYDevicesSet.has(r.device_name))
            tempYDevicesSet.add(r.device_name)
    })

    const chartData = {
        [REPORT_STATUSES.info]: new Map(),
        [REPORT_STATUSES.warning]: new Map(),
        [REPORT_STATUSES.critical]: new Map(),
        [REPORT_STATUSES.infested]: new Map(),
        [REPORT_STATUSES.down]: new Map(),
        [REPORT_STATUSES.catch]: new Map()
    }

    for (let [key, value] of tempXDatesMap) {
        value.forEach(r => {
            const reportStatus = getReportStatus(r)
            if (!chartData[reportStatus].has(`${key}-${r.device_name}`)) {
                chartData[reportStatus].set(`${key}-${r.device_name}`, {
                    x: key,
                    y: r.device_name,
                    dataLength: 1,
                    data: [r]
                })
            } else {
                chartData[reportStatus].get(
                    `${key}-${r.device_name}`
                ).dataLength += 1
                chartData[reportStatus]
                    .get(`${key}-${r.device_name}`)
                    .data.push(r)
            }
        })
    }

    for (const status in REPORT_STATUSES) {
        if (REPORT_STATUSES.hasOwnProperty(status)) {
            chartData[REPORT_STATUSES[status]] = Array.from(
                chartData[REPORT_STATUSES[status]],
                ([key, value]) => ({
                    x: value.x,
                    y: value.y,
                    data: value.data,
                    dataLength: value.dataLength,
                    label: value.data.map(r => {
                        const reportTypeText = getReportTypeText(r, locale)
                        return `${
                            r.device_name
                        }: ${reportTypeText}: ${getFormattedDate(r.created_at)}`
                    })
                })
            )
        }
    }

    return {
        chartData,
        dateDomain: Array.from(tempXDatesMap.keys()),
        deviceDomain: Array.from(tempYDevicesSet)
    }
}

export function getAvailableDevicesToFloorPlans(
    devices,
    // floorPlanDevices,
    locale
) {
    if (!devices) return {}

    // const floorPlanDevicesMap = floorPlanDevices.reduce(
    //     (acc, d) => ({ ...acc, [d.id]: d }),
    //     {}
    // )
    // const devicesFiltered = devices.filter(
    //     d => !floorPlanDevicesMap.hasOwnProperty(d.id)
    // )

    const availableDevices = devices.reduce((acc, d) => {
        if (!acc.hasOwnProperty(d.location_id)) {
            acc[d.location_id] = {
                id: d.location_id,
                name: d.location_name,
                isLocation: true,
                children: []
            }
        }
        acc[d.location_id].children.push(d)
        return acc
    }, {})

    const availableDevicesTree = {
        id: LOCATIONS_TREE_ROOT_NODE_ID,
        name: locale.locations.locations,
        isLocation: true,
        children: Object.values(availableDevices)
    }

    return {
        availableDevicesTree
    }
}

function addDepthLevel(arr, depth = 1) {
    arr.forEach(obj => {
        obj.depth = depth
        addDepthLevel(obj.children, depth + 1)
    })
}

function createBottomPaddingNode(fakeId) {
    const id = `bottom_padding_node_${fakeId}`
    return {
        id: id,
        location: {
            id: id,
            is_bottom_padding_node: true
        }
    }
}

export function flattenTree(tree, key, collection) {
    if (!tree[key] || tree[key].length === 0) return

    tree[key].forEach(el => {
        const { children, ...rest } = el
        collection.push({ ...rest, hasChildren: Boolean(children.length) })
        flattenTree(el, key, collection)
    })

    return
}

function getPickerCustomersForLocationCreation(p, locations) {
    return Object.keys(p).reduce((acc, cId) => {
        if (
            p[cId].hasOwnProperty('locations') &&
            p[cId].locations.includes('c') &&
            locations?.some(l => l.customer_id === Number(cId))
        ) {
            acc[cId] = p[cId].customer_name
        }
        return acc
    }, {})
}

export const getLocationsTreeSelector = createSelector(
    state => state.locations.locations,
    state => state.main.locale,
    (locations, locale) => getLocationsTree(locations, locale, true)
)

export const getPickerLocationsSelector = createSelector(
    state => state.locations.pickerLocationsList,
    state => state.locations.location,
    (locations, location) => getPickerLocations(locations, location)
)

export const getPickerCustomersForLocationCreationSelector = createSelector(
    state => state.main.p,
    state => state.locations.locations,
    (p, locations) => getPickerCustomersForLocationCreation(p, locations)
)
